import { isNil } from '@lucidlink/tscore';
import { parseWasmResult } from '#wasm/common';
export class KeyValueHelpersWasm {
    mod;
    constructor(mod) {
        this.mod = mod;
    }
    makeGlobalConfigKey(setting, encryption) {
        return parseWasmResult(this.mod.MakeConfigKey(encryption, 0, // global, i.e. node ID
        setting.target, setting.name));
    }
    makeRootAccessKey(principalId, fsVersion) {
        return parseWasmResult(this.mod.MakeRootAccessKey(principalId, fsVersion));
    }
    makeFullRootAccessValue(rootFwdKey, rootBwdKey) {
        return parseWasmResult(this.mod.MakeFullAccessValue('', rootFwdKey, rootBwdKey));
    }
    // Creator side: only these named pieces need the root keys - per rescue directory the name
    // HMAC and the encrypted FWD row value - plus the rescue root's own keys for the setting.
    makeLostAndFoundInitCryptoMaterial(rootFwdKey, rootBwdKey, fsVersion) {
        return parseWasmResult(this.mod.MakeLostAndFoundInitCryptoMaterial(rootFwdKey, rootBwdKey, fsVersion));
    }
    // Committer side: assembles the full rescue tree rows from the creator-built material;
    // involves no crypto.
    makeLostAndFoundTreeKeyValues(dirNameHmac, dirFwdValue, subdirNameHmac, subdirFwdValue, fsVersion) {
        return parseWasmResult(this.mod.MakeLostAndFoundTreeKeyValues(dirNameHmac, dirFwdValue, subdirNameHmac, subdirFwdValue, fsVersion));
    }
    // Mirrors C++ SerializeRestrictedDirCryptoKeys: concatenates fwd||bwd into the flat byte
    // string that RestrictedDirCryptoSettingParser expects on the read path.
    makeRestrictedDirCryptoKeyData(fwd, bwd) {
        const result = new Uint8Array(fwd.length + bwd.length);
        result.set(fwd);
        result.set(bwd, fwd.length);
        return result;
    }
    makeTelemetryRecord(nodeId, telemetryName, username, tuple) {
        return parseWasmResult(this.mod.MakeTelemetryRecord(nodeId, telemetryName, username, tuple));
    }
    makeRawTelemetryRecord(nodeId, telemetryName, value) {
        return parseWasmResult(this.mod.MakeRawTelemetryRecord(nodeId, telemetryName, value));
    }
    makeGlobalMetaKey(inodeId) {
        return parseWasmResult(this.mod.MakeGlobalMetaKey(inodeId));
    }
    makeLayoutKeyRange(inodeId) {
        return parseWasmResult(this.mod.MakeLayoutKeyRange(inodeId));
    }
    parseMetadata(globalMetadataValue) {
        return parseWasmResult(this.mod.ParseMetadata(globalMetadataValue));
    }
    parseBasePointer(layoutKvs) {
        return parseWasmResult(this.mod.ParseBasePointer(Array.from(layoutKvs)));
    }
    makeUserActivityRecord(principalName, nodeId, lastActiveTime) {
        return parseWasmResult(this.mod.MakeUserActivityRecord(principalName, nodeId, lastActiveTime.toString()));
    }
    getSchedulesRange() {
        return parseWasmResult(this.mod.GetSchedulesRange());
    }
    getDataStoresRange() {
        return parseWasmResult(this.mod.GetDataStoresKeyRange());
    }
    makeTelemetryCreateScheduleTuple(label, start, interval, retention) {
        return parseWasmResult(this.mod.MakeCreateScheduleTelemetryTuple(label, start.toString(), interval, retention));
    }
    makeTelemetryDeleteScheduleTuple(label) {
        return parseWasmResult(this.mod.MakeTupleFromString(label));
    }
    makeTelemetryCreateSnapshotTuple(label) {
        return parseWasmResult(this.mod.MakeTupleFromString(label));
    }
    makeTelemetryDeleteSnapshotTuple(id) {
        return parseWasmResult(this.mod.MakeTupleFromUint32(id));
    }
    makeTelemetryGrantAccessTuple(principalId, entryId, permissions) {
        return parseWasmResult(this.mod.MakeGrantAccessTelemetryTuple(principalId, entryId, permissions));
    }
    makeTelemetryRevokeAccessTuple(principalId, entryId) {
        return parseWasmResult(this.mod.MakeRevokeAccessTelemetryTuple(principalId, entryId));
    }
    makeSetConfigTelemetryTuple(settings) {
        const serializedSettings = settings.map((setting) => {
            return {
                target: setting.fullName().target,
                name: setting.fullName().name,
                value: setting.rawValue(),
            };
        });
        return parseWasmResult(this.mod.MakeSetConfigTelemetryTuple(serializedSettings));
    }
    makeUnsetConfigTelemetryTuple(settings) {
        return parseWasmResult(this.mod.MakeUnsetConfigTelemetryTuple(settings));
    }
    makeIoStatsKey(nodeId, statsType, statsSubType) {
        if (isNil(statsSubType)) {
            return parseWasmResult(this.mod.MakeIoStatsKey(nodeId, statsType));
        }
        return parseWasmResult(this.mod.MakeIoStatsKeyWithSubType(nodeId, statsType, statsSubType));
    }
    makeIoStatsValue(statsValue) {
        return parseWasmResult(this.mod.MakeIoStatsValue(statsValue.toString()));
    }
    getIoStatsType(statsKey) {
        return parseWasmResult(this.mod.GetIoStatsType(statsKey));
    }
    getIoStatsRange(nodeId) {
        return parseWasmResult(this.mod.GetIoStatsRange(nodeId));
    }
    makeWebFsFsIoStatsKey(nodeId, operation, fileType) {
        return parseWasmResult(this.mod.MakeWebFsFsIoStatsKey(nodeId, operation, fileType));
    }
    makeFsIoStatsValue(statsValue) {
        return parseWasmResult(this.mod.MakeFsIoStatsValue(statsValue.toString()));
    }
    getFsIoStatId(statsKey) {
        return parseWasmResult(this.mod.GetFsIoStatId(statsKey));
    }
    getWebFsFsIoStatsRange(nodeId) {
        return parseWasmResult(this.mod.GetWebFsFsIoStatsRange(nodeId));
    }
}
